import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  MDBDropdown,
  MDBDropdownItem,
  MDBDropdownMenu,
  MDBDropdownToggle,
  MDBTabsPane,
  MDBInput,
} from "mdb-react-ui-kit";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { exportToPDF, exportToCSV } from "../../../helper/exportDatatable";
import { format, parseISO } from "date-fns";
import { usePdfUrls } from "../../../hooks/usePdfUrls";

const Fiscal = ({ active, bill, ordinal, legislatureOrdinal }) => {
  const [fiscalData, setFiscalData] = useState([]);
  const [billFiscalNotes, setBillFiscalNotes] = useState([]);
  const [billFiscalNotesRebuttals, setBillFiscalNotesRebuttals] = useState([]);
  const [globalFilter, setGlobalFilter] = useState(null);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    date: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.CONTAINS },
    documents: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const { handleDocumentClick, fetchPdfUrl } = usePdfUrls();

  const params = useMemo(() => {
    const paramsObj = new URLSearchParams();
    paramsObj.append("legislatureOrdinal", `${legislatureOrdinal}`);
    paramsObj.append("sessionOrdinal", `${ordinal}`);
    paramsObj.append("billType", `${bill.billType.code}`);
    paramsObj.append("billNumber", `${bill.billNumber}`);
    return paramsObj;
  }, [ordinal, legislatureOrdinal, bill.billType, bill.billNumber]);

  const convertISOToReadableTimeFormat = (date) =>
    format(parseISO(date), "MM/dd/yy");

  const transformStatusData = (statusArray) => {
    const onlyFiscalCodes =
      process.env.REACT_APP_FISCAL_NOTE_CODES.split(",").map(Number);
    const uniqueStatuses = new Map();

    statusArray.forEach((statusItem) => {
      const code = parseInt(statusItem?.billStatusCode?.code, 10);
      if (
        onlyFiscalCodes.includes(code) &&
        !uniqueStatuses.has(statusItem.billStatusCode.name)
      ) {
        uniqueStatuses.set(statusItem.billStatusCode.name, {
          date: convertISOToReadableTimeFormat(statusItem.timeStamp),
          status: statusItem.billStatusCode.name,
        });
      }
    });

    return Array.from(uniqueStatuses.values());
  };

  useEffect(() => {
    setFiscalData(transformStatusData(bill.draft.billStatuses));
  }, [bill.draft.billStatuses]);

  const fetchFiscalLinks = async (url, setter) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LAWS_BACKEND_HOST}${url}?${params.toString()}`
      );
      setter(response.data.reverse());
    } catch (error) {
      console.error("Error fetching fiscal links: ", error);
    }
  };

  useEffect(() => {
    fetchFiscalLinks(
      "/docs/v1/documents/getBillFiscalNotes",
      setBillFiscalNotes
    );
  }, [bill]);

  useEffect(() => {
    fetchFiscalLinks(
      "/docs/v1/documents/getBillFiscalNotesRebuttals",
      setBillFiscalNotesRebuttals
    );
  }, [bill]);

  useEffect(() => {
    const combinedData = [
      ...transformStatusData(bill.draft.billStatuses),
      ...billFiscalNotes.map((note) => ({
        status: "Fiscal Note",
        document: (
            <a style={{ cursor: 'pointer' }} onClick={(e) => handleDocumentClick(note.id, e)}>
            {note.fileName}
          </a>
        ),
        documentId: note.id,
        fileName: note.fileName
      })),
      ...billFiscalNotesRebuttals.map((note) => ({
        status: "Rebuttal",
        document: (
            <a style={{ cursor: 'pointer' }} onClick={(e) => handleDocumentClick(note.id, e)}>
            {note.fileName}
          </a>
        ),
        documentId: note.id,
        fileName: note.fileName
      })),
    ];
    setFiscalData(combinedData);
  }, [bill.draft.billStatuses, billFiscalNotes, billFiscalNotesRebuttals, handleDocumentClick]);

  const onGlobalFilterChange = (e) => {
    setFilters({
      global: { value: e.target.value, matchMode: FilterMatchMode.CONTAINS },
    });
  };

  const exportColumns = [
    { field: "date", header: "Submitted Date" },
    { field: "status", header: "Action" },
    { field: "fileName", header: "Documents" },
  ];

  const handlePDFExport = () => {
    const exportData = fiscalData.map(item => ({
      date: item.date || '',
      status: item.status,
      fileName: item.fileName || ''
    }));

    exportToPDF(
      "Fiscal",
      `${bill?.billType?.code}${bill?.billNumber}`,
      exportColumns,
      exportData,
      {
        columnStyles: null,
      }
    );
  };

  const handleCSVExport = async () => {
    const exportData = await Promise.all(
      fiscalData.map(async (item) => {
        const pdfUrl = item.documentId ? await fetchPdfUrl(item.documentId) : null;
        return {
          Date: item.date || '',
          Action: item.status,
          Document: item.fileName || '',
          URL: pdfUrl || 'Not Available'
        };
      })
    );

    exportToCSV(
      "Fiscal",
      `${bill?.billType?.code}${bill?.billNumber}`,
      [
        { field: "Date", header: "Date" },
        { field: "Action", header: "Action" },
        { field: "Document", header: "Document" },
        { field: "URL", header: "URL" },
      ],
      exportData
    );
  };

  return (
    <MDBTabsPane show={active}>
      <div className="row g-1">
        <div className="col-sm"></div>
        <div className="col-sm">
          <MDBInput
            className="mb-3"
            label="Search Fiscal..."
            type="text"
            value={filters.global.value || ""}
            onChange={onGlobalFilterChange}
          />
        </div>
        <div className="col-auto">
          <MDBDropdown className="d-flex justify-content-end mb-3">
            <MDBDropdownToggle>
              <i
                className="fa-light fa-cloud-arrow-down"
                aria-hidden="true"
              ></i>{" "}
              Export
            </MDBDropdownToggle>
            <MDBDropdownMenu>
              <MDBDropdownItem
                link
                onClick={(e) => {
                  e.preventDefault();
                  handleCSVExport();
                }}
              >
                Excel/CSV
              </MDBDropdownItem>
              <MDBDropdownItem
                link
                onClick={(e) => {
                  e.preventDefault();
                  handlePDFExport();
                }}
              >
                Print/PDF
              </MDBDropdownItem>
            </MDBDropdownMenu>
          </MDBDropdown>
        </div>
      </div>
      <DataTable
        value={fiscalData}
        paginator
        rows={100}
        rowsPerPageOptions={[25, 50, 100]}
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
        stripedRows
        columnResizeMode="expand"
        sortField="date"
        sortOrder={1}
        removableSort
        filters={filters}
        globalFilter={globalFilter}
      >
        <Column field="date" header="Action Date" sortable filter />
        <Column field="status" header="Action" sortable filter />
        <Column
          field="document"
          header="Documents"
          sortable
          filter
          body={(rowData) => rowData.document}
        />
      </DataTable>
    </MDBTabsPane>
  );
};

export default Fiscal;
